import React, { useMemo, useState, useEffect } from "react";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Tabs,
  Tag,
  FormInstance,
  Alert,
  Divider,
} from "antd";
import dayjs from "dayjs";
import { RadioCardGroup } from "./RadioCardGroup";
import { calculateDuration } from "../../../utils/duration";
import { FrontDeskBooking, Offer } from "../../../types";
import {
  frontDeskService,
  adminLaundryService,
} from "../../../services/adminService";

interface EditBookingModalProps {
  open: boolean;
  onCancel: () => void;
  form: FormInstance;
  activeTab: string;
  setActiveTab: (key: string) => void;
  onSubmit: (values: any) => void;
  loading: boolean;
  isEditValid: boolean;
  editValidationErrors?: string[];
  selectedEditBooking:
    | (FrontDeskBooking & {
        children?: FrontDeskBooking[];
        is_group_parent?: boolean;
      })
    | null;
  roomTypes: any[];
  roomAvailabilities: Record<string, any>;
  blockedDates: string[];
  editCheckInDate: dayjs.Dayjs | undefined;
  editCheckOutDate: dayjs.Dayjs | undefined;
  editRoomsList: any[];
  offers: Offer[];
  rooms: any[];
}

export const EditBookingModal: React.FC<EditBookingModalProps> = ({
  open,
  onCancel,
  form,
  activeTab,
  setActiveTab,
  onSubmit,
  loading,
  isEditValid,
  editValidationErrors = [],
  selectedEditBooking,
  roomTypes,
  roomAvailabilities,
  blockedDates,
  editCheckInDate,
  editCheckOutDate,
  editRoomsList,
  offers,
  rooms,
}) => {
  const [finesBreakdown, setFinesBreakdown] = useState<
    { roomNo: string; inspId: string; amount: number }[]
  >([]);
  const [laundryBreakdown, setLaundryBreakdown] = useState<
    { roomNo: string; orderNo: string; amount: number; service_type: string }[]
  >([]);

  useEffect(() => {
    const fetchFines = async () => {
      if (!open || !selectedEditBooking) return;
      try {
        const breakdown: {
          roomNo: string;
          inspId: string;
          amount: number;
        }[] = [];

        if (
          selectedEditBooking.is_group_parent &&
          selectedEditBooking.children &&
          selectedEditBooking.children.length > 0
        ) {
          for (const child of selectedEditBooking.children) {
            const realId = child.id.split("-child-")[0];
            const res: any = await frontDeskService.getBookingFines(realId);
            if (res.data && res.data.length > 0) {
              const amount = res.data.reduce(
                (sum: number, f: any) => sum + Number(f.amount),
                0,
              );
              if (amount > 0) {
                breakdown.push({
                  roomNo: child.room_number || "N/A",
                  inspId: child.inspection_number || "N/A",
                  amount,
                });
              }
            }
          }
        } else {
          const realId = selectedEditBooking.id.split("-child-")[0];
          const res: any = await frontDeskService.getBookingFines(realId);
          if (res.data && res.data.length > 0) {
            const amount = res.data.reduce(
              (sum: number, f: any) => sum + Number(f.amount),
              0,
            );
            if (amount > 0) {
              breakdown.push({
                roomNo: selectedEditBooking.room_number || "N/A",
                inspId: selectedEditBooking.inspection_number || "N/A",
                amount,
              });
            }
          }
        }
        setFinesBreakdown(breakdown);

        const laundryRows: {
          roomNo: string;
          orderNo: string;
          amount: number;
          service_type: string;
        }[] = [];
        const bookingsToCheck =
          selectedEditBooking.is_group_parent && selectedEditBooking.children
            ? selectedEditBooking.children
            : [selectedEditBooking];
        for (const b of bookingsToCheck) {
          try {
            const realId = b.id.split("-child-")[0];
            const lRes: any =
              await adminLaundryService.getOrdersByBooking(realId);
            const orders: any[] = Array.isArray(lRes.data) ? lRes.data : [];
            for (const order of orders) {
              if (order.status === "cancelled") continue;
              laundryRows.push({
                roomNo: b.room_number || "N/A",
                orderNo: order.order_number,
                amount: Number(order.total_amount || 0),
                service_type: order.service_type || 'laundry',
              });
            }
          } catch (_) {}
        }
        setLaundryBreakdown(laundryRows);
      } catch (err) {
        console.error("Failed to fetch fines for details view", err);
      }
    };
    fetchFines();
  }, [open, selectedEditBooking]);

  const idDocumentType = Form.useWatch("id_document_type", form);

  const handleEditNext = async () => {
    try {
      if (activeTab === "1") {
        await form.validateFields([
          "guest_name",
          "email",
          "phone",
          "address_line_1",
          "address_line_2",
          "id_document_type",
          "id_document_number",
        ]);
      } else if (activeTab === "2") {
        await form.validateFields([
          "reservation_source",
          "rooms",
          "check_in_date",
          "check_out_date",
        ]);
      }
      setActiveTab(String(Number(activeTab) + 1));
    } catch (e) {
      // Validation failed
    }
  };

  const earlyCheckInFee = Form.useWatch("early_check_in_fee", form) || 0;
  const lateCheckOutFee = Form.useWatch("late_check_out_fee", form) || 0;

  const nights =
    editCheckInDate && editCheckOutDate
      ? Math.max(editCheckOutDate.diff(editCheckInDate, "days"), 0)
      : 0;

  const now = useMemo(() => new Date(), []);

  const getActiveOffer = useMemo(() => {
    return (roomTypeId: string, acStatus?: string): Offer | null => {
      return (
        offers.find(
          (o) =>
            o.room_type_id === roomTypeId &&
            o.is_active &&
            new Date(o.start_date) <= now &&
            new Date(o.end_date) >= now &&
            (!o.ac_status || o.ac_status === 'any' || o.ac_status === acStatus)
        ) || null
      );
    };
  }, [offers, now]);

  let roomTotal = 0;
  let extraBedsTotal = 0;
  let totalOriginalRoomPrice = 0;
  let hasAnyDiscount = false;

  const roomBreakdowns: Array<{
    roomTypeName: string;
    quantity: number;
    originalPrice: number;
    discountedPrice: number;
    offerTitle: string | null;
    nights: number;
  }> = [];

  editRoomsList.forEach((r) => {
    if (r.room_type_id && r.quantity) {
      const roomType = roomTypes.find((rt) => rt.id === r.room_type_id);
      if (roomType) {
        let originalPrice = Number(roomType.base_price || 0);
        if (roomType.ac_status_preference === 'both') {
          if (r.ac_status === 'ac') originalPrice = Number((roomType as any).ac_base_price || roomType.base_price || 0);
          else if (r.ac_status === 'non_ac') originalPrice = Number((roomType as any).non_ac_base_price || roomType.base_price || 0);
        }
        let basePricePerNight = originalPrice;
        let offerTitle: string | null = null;

        if (
          (r as any).original_room_price !== undefined &&
          (r as any).original_room_price !== null
        ) {
          originalPrice = Number((r as any).original_room_price);
          basePricePerNight = Number((r as any).discounted_price);
          offerTitle = (r as any).offer_title || null;
        } else {
          const activeOffer = getActiveOffer(r.room_type_id, r.ac_status);
          if (activeOffer) {
            if (activeOffer.discount_amount) {
              basePricePerNight = Math.max(
                0,
                basePricePerNight - Number(activeOffer.discount_amount),
              );
            } else if (activeOffer.discount_percentage) {
              basePricePerNight = Math.round(
                basePricePerNight *
                  (1 - Number(activeOffer.discount_percentage) / 100),
              );
            }
            offerTitle = activeOffer.title;
          }
        }

        if (basePricePerNight < originalPrice) {
          hasAnyDiscount = true;
        }

        totalOriginalRoomPrice += originalPrice * Number(r.quantity) * nights;
        roomTotal += basePricePerNight * Number(r.quantity) * nights;

        roomBreakdowns.push({
          roomTypeName: roomType.name,
          quantity: Number(r.quantity),
          originalPrice,
          discountedPrice: basePricePerNight,
          offerTitle,
          nights,
        });

        if (r.extra_beds) {
          extraBedsTotal +=
            Number(roomType.extra_bed_price || 0) *
            Number(r.extra_beds) *
            nights;
        }
      }
    }
  });

  const discountType = Form.useWatch("discount_type", form) || "none";
  const discountValue = Form.useWatch("discount_value", form) || 0;
  const enableVat = Form.useWatch("enable_vat", form) || "no";
  const vatPercentage = Form.useWatch("vat_percentage", form) || 0;

  const serviceChargeType =
    Form.useWatch("service_charge_type", form) || "none";
  const serviceChargeValue = Form.useWatch("service_charge_value", form) || 0;

  const roomSubtotal = roomTotal + extraBedsTotal;
  const total1 =
    roomSubtotal + Number(earlyCheckInFee) + Number(lateCheckOutFee);

  let manualDiscountDeduction = 0;
  if (discountType === "fixed" && Number(discountValue) > 0) {
    manualDiscountDeduction = Number(discountValue);
  } else if (discountType === "percentage" && Number(discountValue) > 0) {
    manualDiscountDeduction = Math.round(
      (total1 * Number(discountValue)) / 100,
    );
  }

  const total2 = Math.max(0, total1 - manualDiscountDeduction);

  let serviceCharge = 0;
  if (serviceChargeType === "fixed" && Number(serviceChargeValue) > 0) {
    serviceCharge = Number(serviceChargeValue);
  } else if (
    serviceChargeType === "percentage" &&
    Number(serviceChargeValue) > 0
  ) {
    serviceCharge = Math.round((total2 * Number(serviceChargeValue)) / 100);
  }

  const total3 = total2 + serviceCharge;

  const taxValue =
    enableVat === "yes"
      ? Math.round(total3 * (Number(vatPercentage) / 100))
      : 0;
  const finalTotal = total3 + taxValue;

  return (
    <Modal
      open={open}
      title={`Edit Booking - ${selectedEditBooking?.guest_name || "Group"}`}
      onCancel={onCancel}
      width={760}
      footer={
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Button
            onClick={() => {
              form.resetFields();
              setActiveTab("1");
            }}
          >
            Reset
          </Button>
          <Space>
            {activeTab !== "1" && (
              <Button
                onClick={() => setActiveTab(String(Number(activeTab) - 1))}
              >
                Back
              </Button>
            )}
            {activeTab !== "3" && (
              <Button
                id="edit-next-btn"
                type="primary"
                onClick={handleEditNext}
              >
                Next
              </Button>
            )}
            {activeTab === "3" && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  gap: "8px",
                }}
              >
                {editValidationErrors.length > 0 && (
                  <Alert
                    type="error"
                    message="Cannot Save"
                    description={
                      <ul
                        style={{
                          margin: 0,
                          paddingLeft: "16px",
                          fontSize: "12px",
                        }}
                      >
                        {editValidationErrors.map((err, i) => (
                          <li key={i}>{err}</li>
                        ))}
                      </ul>
                    }
                    showIcon
                    style={{
                      padding: "4px 12px",
                      textAlign: "left",
                      maxWidth: "400px",
                    }}
                  />
                )}
                <Button
                  id="edit-submit-btn"
                  type="primary"
                  loading={loading}
                  onClick={() => form.submit()}
                  disabled={!isEditValid}
                >
                  Save Changes
                </Button>
              </div>
            )}
          </Space>
        </div>
      }
    >
      <Form form={form} layout="vertical" onFinish={onSubmit}>
        <Tabs
          activeKey={activeTab}
          onTabClick={(key) => {
            if (key !== activeTab) {
              const modalBody = document.querySelector(".ant-modal-body");
              if (modalBody) {
                modalBody.scrollTo({
                  top: modalBody.scrollHeight,
                  behavior: "smooth",
                });
              }
              const nextBtn = document.getElementById(
                activeTab === "3" ? "edit-submit-btn" : "edit-next-btn",
              );
              if (nextBtn) {
                nextBtn.focus();
                nextBtn.style.boxShadow = "0 0 0 4px rgba(24, 144, 255, 0.4)";
                nextBtn.style.transition = "box-shadow 0.3s ease";
                setTimeout(() => {
                  nextBtn.style.boxShadow = "";
                }, 1000);
              }
            }
          }}
          items={[
            {
              key: "1",
              label: "1. Guest Details",
              children: (
                <div className="grid-two">
                  <Form.Item
                    name="guest_name"
                    label="Guest Name"
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name="email"
                    label="Email (optional)"
                    rules={[{ type: "email" }]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name="phone"
                    label="Phone"
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name="address_line_1"
                    label="Address Line 1 (optional)"
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name="address_line_2"
                    label="Address Line 2 (optional)"
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name="id_document_type"
                    label="ID Type (optional)"
                    style={{ gridColumn: "1 / -1" }}
                  >
                    <RadioCardGroup
                      options={[
                        { label: "Passport", value: "passport" },
                        { label: "National ID", value: "national_id" },
                        {
                          label: "Driving License",
                          value: "driving_license",
                        },
                      ]}
                    />
                  </Form.Item>
                  {idDocumentType && (
                    <Form.Item
                      name="id_document_number"
                      label={`${idDocumentType === "passport" ? "Passport" : idDocumentType === "national_id" ? "National ID" : "Driving License"} Number (optional)`}
                      style={{ gridColumn: "1 / -1" }}
                    >
                      <Input />
                    </Form.Item>
                  )}
                </div>
              ),
            },
            {
              key: "2",
              label: "2. Room & Dates",
              children: (
                <div className="grid-two">
                  <Form.Item
                    name="reservation_source"
                    label="Reservation Source"
                    initialValue="walk_in"
                    rules={[{ required: true }]}
                    style={{ gridColumn: "1 / -1" }}
                  >
                    <RadioCardGroup
                      options={[
                        { label: "Walk-In", value: "walk_in" },
                        { label: "Direct", value: "direct" },
                        { label: "Website", value: "website" },
                        { label: "Booking.com", value: "booking_com" },
                        { label: "Agoda", value: "agoda" },
                        { label: "Expedia", value: "expedia" },
                        { label: "Travel Agent", value: "travel_agent" },
                        { label: "Phone", value: "phone" },
                        { label: "WhatsApp", value: "whatsapp" },
                        { label: "Other", value: "other" },
                      ]}
                    />
                  </Form.Item>

                  <Form.Item
                    name="check_in_date"
                    label="Check-In Date"
                    rules={[{ required: true }]}
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      disabledDate={(currentDate) => {
                        if (
                          currentDate &&
                          blockedDates.includes(
                            currentDate.format("YYYY-MM-DD"),
                          )
                        ) {
                          return true;
                        }
                        return false;
                      }}
                    />
                  </Form.Item>
                  <Form.Item
                    name="check_out_date"
                    label="Check-Out Date"
                    rules={[{ required: true }]}
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      disabledDate={(currentDate) => {
                        const checkInVal = form.getFieldValue("check_in_date");
                        if (
                          checkInVal &&
                          currentDate &&
                          currentDate.isBefore(
                            dayjs(checkInVal).add(1, "day").startOf("day"),
                          )
                        ) {
                          return true;
                        }
                        if (
                          currentDate &&
                          blockedDates.includes(
                            currentDate.format("YYYY-MM-DD"),
                          )
                        ) {
                          return true;
                        }
                        return false;
                      }}
                    />
                  </Form.Item>

                  <div style={{ gridColumn: "1 / -1" }}>
                    {!(editCheckInDate && editCheckOutDate) && (
                      <Alert
                        message="Please select the dates to select rooms"
                        type="info"
                        showIcon
                        style={{ marginBottom: "16px" }}
                      />
                    )}
                    <Form.List
                      name="rooms"
                      initialValue={[
                        { room_type_id: undefined, quantity: 1, extra_beds: 0 },
                      ]}
                    >
                      {(fields, { add, remove }) => (
                        <>
                          {fields.map(({ key, name, ...restField }, index) => (
                            <div
                              key={key}
                              style={{
                                display: "flex",
                                gap: "16px",
                                alignItems: "flex-start",
                                marginBottom: "16px",
                                background: "#f9f9f9",
                                padding: "16px",
                                borderRadius: "8px",
                                flexWrap: "wrap",
                                opacity: !(editCheckInDate && editCheckOutDate)
                                  ? 0.6
                                  : 1,
                              }}
                            >
                              <div style={{ flex: 1, minWidth: "200px" }}>
                                <Form.Item
                                  {...restField}
                                  name={[name, "room_type_id"]}
                                  label={`Room ${index + 1} Type`}
                                  rules={[
                                    {
                                      required: true,
                                      message: "Missing room type",
                                    },
                                  ]}
                                  style={{ marginBottom: "12px" }}
                                >
                                  <Select
                                    placeholder="Select a room type"
                                    disabled={
                                      !(editCheckInDate && editCheckOutDate)
                                    }
                                  >
                                    {roomTypes.map((rt) => (
                                      <Select.Option key={rt.id} value={rt.id}>
                                        {rt.name} (Max {rt.max_occupancy})
                                      </Select.Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                                {editRoomsList[index]?.room_type_id &&
                                  roomAvailabilities[
                                    editRoomsList[index].room_type_id
                                  ] && (
                                    <div style={{ marginBottom: "12px" }}>
                                      <Tag
                                        color={
                                          roomAvailabilities[
                                            editRoomsList[index].room_type_id
                                          ].isAvailable &&
                                          roomAvailabilities[
                                            editRoomsList[index].room_type_id
                                          ].availableCount >=
                                            (editRoomsList[index].quantity || 1)
                                            ? "green"
                                            : "red"
                                        }
                                      >
                                        {roomAvailabilities[
                                          editRoomsList[index].room_type_id
                                        ].isAvailable
                                          ? `Available: ${roomAvailabilities[editRoomsList[index].room_type_id].availableCount} free`
                                          : "Fully Booked for Selected Dates"}
                                      </Tag>
                                    </div>
                                  )}

                                {editCheckInDate &&
                                  editCheckOutDate &&
                                  !editRoomsList[index]?.room_type_id && (
                                    <div
                                      style={{
                                        fontSize: "12px",
                                        color: "#888",
                                        marginBottom: "8px",
                                        fontStyle: "italic",
                                      }}
                                    >
                                      * Select a room type to enter quantity and
                                      extra beds
                                    </div>
                                  )}

                                <div
                                  className="grid-three"
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr 1fr",
                                    gap: "16px",
                                  }}
                                >
                                  <Form.Item
                                    {...restField}
                                    name={[name, "quantity"]}
                                    label="Quantity"
                                    rules={[{ required: true }]}
                                    style={{ marginBottom: 0 }}
                                  >
                                    <InputNumber
                                      min={1}
                                      max={
                                        editRoomsList[index]?.room_type_id &&
                                        roomAvailabilities[
                                          editRoomsList[index].room_type_id
                                        ]?.isAvailable
                                          ? roomAvailabilities[
                                              editRoomsList[index].room_type_id
                                            ].availableCount
                                          : undefined
                                      }
                                      style={{ width: "100%" }}
                                      disabled={
                                        !(
                                          editCheckInDate && editCheckOutDate
                                        ) || !editRoomsList[index]?.room_type_id
                                      }
                                    />
                                  </Form.Item>
                                  <Form.Item
                                    {...restField}
                                    name={[name, "extra_beds"]}
                                    label="Extra Beds"
                                    style={{ marginBottom: 0 }}
                                  >
                                    <InputNumber
                                      min={0}
                                      style={{ width: "100%" }}
                                      disabled={
                                        !(
                                          editCheckInDate && editCheckOutDate
                                        ) || !editRoomsList[index]?.room_type_id
                                      }
                                    />
                                  </Form.Item>
                                  <Form.Item
                                    {...restField}
                                    name={[name, "number_of_guests"]}
                                    label="Total Guests"
                                    rules={[{ required: true }]}
                                    style={{ marginBottom: 0 }}
                                    extra={
                                      editRoomsList[index]?.room_type_id &&
                                      roomTypes.find(
                                        (rt) =>
                                          rt.id ===
                                          editRoomsList[index].room_type_id,
                                      ) ? (
                                        <Tag
                                          color="blue"
                                          style={{ marginTop: "8px" }}
                                        >
                                          Max Occupancy:{" "}
                                          {(roomTypes.find(
                                            (rt) =>
                                              rt.id ===
                                              editRoomsList[index].room_type_id,
                                          )?.max_occupancy || 1) *
                                            (editRoomsList[index]?.quantity ||
                                              1) +
                                            (editRoomsList[index]?.extra_beds ||
                                              0)}
                                        </Tag>
                                      ) : undefined
                                    }
                                  >
                                    <InputNumber
                                      min={1}
                                      max={
                                        editRoomsList[index]?.room_type_id &&
                                        roomTypes.find(
                                          (rt) =>
                                            rt.id ===
                                            editRoomsList[index].room_type_id,
                                        )
                                          ? (roomTypes.find(
                                              (rt) =>
                                                rt.id ===
                                                editRoomsList[index]
                                                  .room_type_id,
                                            )?.max_occupancy || 1) *
                                              (editRoomsList[index]?.quantity ||
                                                1) +
                                            (editRoomsList[index]?.extra_beds ||
                                              0)
                                          : undefined
                                      }
                                      style={{ width: "100%" }}
                                      disabled={
                                        !(
                                          editCheckInDate && editCheckOutDate
                                        ) || !editRoomsList[index]?.room_type_id
                                      }
                                    />
                                  </Form.Item>
                                </div>
                                {editRoomsList[index]?.room_type_id && (() => {
                                  const rtId = editRoomsList[index].room_type_id;

                                  const typeRooms = rooms.filter(r => r.room_type_id === rtId && r.status !== 'maintenance' && r.status !== 'out_of_order' && r.status !== 'out_of_service');
                                  const hasAC = typeRooms.some(r => r.ac_status === 'ac' || r.ac_status === 'both');
                                  const hasNonAC = typeRooms.some(r => r.ac_status === 'non_ac' || r.ac_status === 'both');
                                  
                                  let acOptions: any[] = [];
                                  if (hasAC && hasNonAC) {
                                    acOptions = [
                                      { label: "AC", value: "ac" },
                                      { label: "Non-AC", value: "non_ac" }
                                    ];
                                  } else if (hasAC) {
                                    acOptions = [{ label: "AC Only", value: "ac" }];
                                    if (editRoomsList[index].ac_status !== 'ac') {
                                      setTimeout(() => {
                                        const fields = form.getFieldValue('rooms') || [];
                                        fields[index] = { ...fields[index], ac_status: 'ac' };
                                        form.setFieldsValue({ rooms: fields });
                                      }, 0);
                                    }
                                  } else if (hasNonAC) {
                                    acOptions = [{ label: "Non-AC Only", value: "non_ac" }];
                                    if (editRoomsList[index].ac_status !== 'non_ac') {
                                      setTimeout(() => {
                                        const fields = form.getFieldValue('rooms') || [];
                                        fields[index] = { ...fields[index], ac_status: 'non_ac' };
                                        form.setFieldsValue({ rooms: fields });
                                      }, 0);
                                    }
                                  }

                                  if (acOptions.length > 0) {
                                    return (
                                      <div style={{ marginTop: '16px', width: '100%' }}>
                                        <Form.Item
                                          {...restField}
                                          name={[name, "ac_status"]}
                                          label="AC / Non-AC Preference"
                                          rules={[{ required: true, message: 'Please select AC preference' }]}
                                          style={{ marginBottom: 0 }}
                                        >
                                          <RadioCardGroup options={acOptions} />
                                        </Form.Item>
                                      </div>
                                    );
                                  }
                                  
                                  return null;
                                })()}
                              </div>
                              {fields.length > 1 && (
                                <Button
                                  type="text"
                                  danger
                                  icon={<DeleteOutlined />}
                                  onClick={() => remove(name)}
                                  style={{ marginTop: "32px" }}
                                  disabled={
                                    !(editCheckInDate && editCheckOutDate)
                                  }
                                />
                              )}
                            </div>
                          ))}
                          <Form.Item>
                            <Button
                              type="dashed"
                              onClick={() =>
                                add({ quantity: 1, extra_beds: 0 })
                              }
                              block
                              icon={<PlusOutlined />}
                              disabled={!(editCheckInDate && editCheckOutDate)}
                            >
                              Add Another Room
                            </Button>
                          </Form.Item>
                        </>
                      )}
                    </Form.List>
                  </div>
                  {editCheckInDate &&
                    editCheckOutDate &&
                    calculateDuration(editCheckInDate, editCheckOutDate) && (
                      <div style={{ gridColumn: "1 / -1", textAlign: "left" }}>
                        <Tag
                          color="#B79A68"
                          style={{ fontSize: "14px", padding: "4px 8px" }}
                        >
                          Selected Duration:{" "}
                          {calculateDuration(editCheckInDate, editCheckOutDate)}
                        </Tag>
                      </div>
                    )}
                </div>
              ),
            },
            {
              key: "3",
              label: "3. Other Details",
              children: (
                <>
                  <div className="grid-two">
                    <Form.Item
                      name="early_check_in_fee"
                      label="Early Check-In Fee (optional)"
                    >
                      <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                    <Form.Item
                      name="late_check_out_fee"
                      label="Late Check-Out Fee (optional)"
                    >
                      <InputNumber min={0} style={{ width: "100%" }} />
                    </Form.Item>
                  </div>
                  <Divider style={{ margin: "12px 0" }} />
                  <h4
                    style={{
                      margin: "0 0 12px 0",
                      fontSize: "14px",
                      color: "#555",
                    }}
                  >
                    Service Charge (optional)
                  </h4>
                  <div className="grid-two">
                    <Form.Item
                      name="service_charge_type"
                      label="Service Charge Type"
                      initialValue="none"
                    >
                      <RadioCardGroup
                        options={[
                          { label: "None", value: "none" },
                          { label: "Fixed (LKR)", value: "fixed" },
                          { label: "Percentage (%)", value: "percentage" },
                        ]}
                      />
                    </Form.Item>
                    {serviceChargeType !== "none" && (
                      <Form.Item
                        name="service_charge_value"
                        label={
                          serviceChargeType === "fixed"
                            ? "Service Charge Amount (LKR)"
                            : "Service Charge Percentage (%)"
                        }
                      >
                        <InputNumber
                          min={0}
                          max={
                            serviceChargeType === "percentage" ? 100 : undefined
                          }
                          style={{ width: "100%" }}
                          placeholder={
                            serviceChargeType === "fixed"
                              ? "e.g. 500"
                              : "e.g. 10"
                          }
                        />
                      </Form.Item>
                    )}
                  </div>
                  <Form.Item
                    name="special_requests"
                    label="Special Requests (optional)"
                  >
                    <Input.TextArea rows={3} />
                  </Form.Item>
                  <Form.Item
                    name="allocation_note"
                    label="Allocation Note (optional)"
                  >
                    <Input.TextArea rows={2} />
                  </Form.Item>

                  <Divider style={{ margin: "12px 0" }} />
                  <h4
                    style={{
                      margin: "0 0 12px 0",
                      fontSize: "14px",
                      color: "#555",
                    }}
                  >
                    Manual Discount (optional)
                  </h4>
                  <div className="grid-two">
                    <Form.Item
                      name="discount_type"
                      label="Discount Type"
                      initialValue="none"
                    >
                      <RadioCardGroup
                        options={[
                          { label: "None", value: "none" },
                          { label: "Fixed (LKR)", value: "fixed" },
                          { label: "Percentage (%)", value: "percentage" },
                        ]}
                      />
                    </Form.Item>
                    {discountType !== "none" && (
                      <Form.Item
                        name="discount_value"
                        label={
                          discountType === "fixed"
                            ? "Discount Amount (LKR)"
                            : "Discount Percentage (%)"
                        }
                      >
                        <InputNumber
                          min={0}
                          max={discountType === "percentage" ? 100 : undefined}
                          style={{ width: "100%" }}
                          placeholder={
                            discountType === "fixed" ? "e.g. 2000" : "e.g. 10"
                          }
                        />
                      </Form.Item>
                    )}
                  </div>

                  <Divider style={{ margin: "12px 0" }} />
                  <h4
                    style={{
                      margin: "0 0 12px 0",
                      fontSize: "14px",
                      color: "#555",
                    }}
                  >
                    VAT Setup (optional)
                  </h4>
                  <div className="grid-two">
                    <Form.Item
                      name="enable_vat"
                      label="Apply VAT?"
                      initialValue="no"
                    >
                      <RadioCardGroup
                        options={[
                          { label: "No VAT", value: "no" },
                          { label: "Enable VAT", value: "yes" },
                        ]}
                      />
                    </Form.Item>
                    {enableVat === "yes" && (
                      <Form.Item
                        name="vat_percentage"
                        label="VAT Percentage (%)"
                        rules={[
                          {
                            required: true,
                            message: "Please enter VAT percentage",
                          },
                        ]}
                      >
                        <InputNumber
                          min={0}
                          max={100}
                          style={{ width: "100%" }}
                          placeholder="e.g. 18"
                        />
                      </Form.Item>
                    )}
                  </div>

                  {nights > 0 && finalTotal > 0 && (
                    <div
                      style={{
                        marginTop: "24px",
                        padding: "16px",
                        backgroundColor: "#f5f5f5",
                        borderRadius: "8px",
                      }}
                    >
                      <h3 style={{ margin: "0 0 16px 0", fontSize: "16px" }}>
                        Price Breakdown
                      </h3>
                      {roomBreakdowns.map((rb, idx) => (
                        <div
                          key={idx}
                          style={{
                            marginBottom: "12px",
                            padding: "10px 12px",
                            backgroundColor: "#fff",
                            borderRadius: "6px",
                            border: "1px solid #e8e8e8",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: "6px",
                            }}
                          >
                            <span style={{ fontWeight: 600, fontSize: "14px" }}>
                              {rb.roomTypeName}
                              {rb.offerTitle && (
                                <Tag
                                  color="green"
                                  style={{
                                    marginLeft: "8px",
                                    fontSize: "11px",
                                  }}
                                >
                                  {rb.offerTitle}
                                </Tag>
                              )}
                            </span>
                            <span style={{ fontWeight: 600, fontSize: "14px" }}>
                              LKR{" "}
                              {(
                                rb.discountedPrice *
                                rb.quantity *
                                rb.nights
                              ).toLocaleString("en-US", {
                                minimumFractionDigits: 2,
                              })}
                            </span>
                          </div>
                          <div
                            style={{
                              fontSize: "12px",
                              color: "#888",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <span>
                              {rb.offerTitle ? (
                                <>
                                  <span
                                    style={{
                                      textDecoration: "line-through",
                                      color: "#bbb",
                                      marginRight: "4px",
                                    }}
                                  >
                                    LKR{" "}
                                    {rb.originalPrice.toLocaleString("en-US", {
                                      minimumFractionDigits: 2,
                                    })}
                                  </span>
                                  <span
                                    style={{
                                      color: "#389e0d",
                                      fontWeight: 500,
                                    }}
                                  >
                                    LKR{" "}
                                    {rb.discountedPrice.toLocaleString(
                                      "en-US",
                                      {
                                        minimumFractionDigits: 2,
                                      },
                                    )}
                                  </span>
                                  {" / night"}
                                </>
                              ) : (
                                <>
                                  LKR{" "}
                                  {rb.discountedPrice.toLocaleString("en-US", {
                                    minimumFractionDigits: 2,
                                  })}
                                  {" / night"}
                                </>
                              )}
                            </span>
                            <span style={{ color: "#666" }}>
                              {rb.quantity}{" "}
                              {rb.quantity === 1 ? "room" : "rooms"} ×{" "}
                              {rb.nights} {rb.nights === 1 ? "night" : "nights"}
                            </span>
                          </div>
                        </div>
                      ))}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "8px",
                        }}
                      >
                        <span>Room Subtotal</span>
                        <span>
                          LKR{" "}
                          {roomTotal.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                      {extraBedsTotal > 0 && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "8px",
                          }}
                        >
                          <span>Extra Beds Charges</span>
                          <span>
                            LKR{" "}
                            {extraBedsTotal.toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      )}
                      {Number(earlyCheckInFee) > 0 && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "8px",
                          }}
                        >
                          <span>Early Check-In Fee</span>
                          <span>
                            LKR{" "}
                            {Number(earlyCheckInFee).toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      )}
                      {Number(lateCheckOutFee) > 0 && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "8px",
                          }}
                        >
                          <span>Late Check-Out Fee</span>
                          <span>
                            LKR{" "}
                            {Number(lateCheckOutFee).toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      )}
                      {Number(serviceCharge) > 0 && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "8px",
                          }}
                        >
                          <span>Service Charge</span>
                          <span>
                            LKR{" "}
                            {Number(serviceCharge).toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      )}
                      {finesBreakdown.map((f, i) => (
                        <div
                          key={`fine-${i}`}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "8px",
                            color: "#cf1322",
                            fontWeight: 500,
                          }}
                        >
                          <span>
                            Inspection Fines
                            {f.inspId !== "N/A" && ` (${f.inspId})`}
                            {selectedEditBooking?.is_group_parent && ` - Rm ${f.roomNo}`}
                          </span>
                          <span>
                            LKR{" "}
                            {Number(f.amount).toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      ))}
                      {laundryBreakdown.map((l, i) => (
                        <div
                          key={`ldy-${i}`}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "8px",
                            color: "#b79a68",
                            fontWeight: 500,
                          }}
                        >
                          <span>
                            {l.service_type === 'ironing' ? 'Ironing' : 'Laundry'} Service ({l.orderNo}){selectedEditBooking?.is_group_parent && ` - Rm ${l.roomNo}`}
                          </span>
                          <span>
                            LKR{" "}
                            {Number(l.amount).toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      ))}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "8px",
                        }}
                      >
                        <span>
                          Taxes & Fees (
                          {enableVat === "yes" ? vatPercentage : 0}%)
                        </span>
                        <span>
                          LKR{" "}
                          {taxValue.toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                      {manualDiscountDeduction > 0 && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            marginBottom: "8px",
                            color: "#389e0d",
                          }}
                        >
                          <span>
                            Manual Discount
                            {discountType === "percentage" && (
                              <span
                                style={{
                                  fontSize: "12px",
                                  color: "#888",
                                  marginLeft: "4px",
                                }}
                              >
                                ({discountValue}%)
                              </span>
                            )}
                          </span>
                          <span>
                            − LKR{" "}
                            {manualDiscountDeduction.toLocaleString("en-US", {
                              minimumFractionDigits: 2,
                            })}
                          </span>
                        </div>
                      )}
                      <Divider style={{ margin: "12px 0" }} />
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontWeight: "bold",
                          fontSize: "16px",
                        }}
                      >
                        <span>Total Price</span>
                        <span>
                          LKR{" "}
                          {(
                            finalTotal +
                            finesBreakdown.reduce((sum, f) => sum + f.amount, 0) +
                            laundryBreakdown.reduce((sum, l) => sum + l.amount, 0)
                          ).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              ),
            },
          ]}
        />
      </Form>
    </Modal>
  );
};
